import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, GraduationCap, AlertTriangle } from "lucide-react";

const slug = (y) => y.replace("/", "-");

const NoConfirm = ({ step, setStep, course, onConfirm }) => (
  <AlertDialog open={step > 0} onOpenChange={(o) => !o && setStep(0)}>
    <AlertDialogContent data-testid="prereg-no-dialog" className="w-[calc(100%-1.5rem)] rounded-lg">
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-destructive" /> Perderai il posto nel corso</AlertDialogTitle>
        <AlertDialogDescription data-testid="prereg-no-text">
          {step === 1 ? `Rispondendo "No" verrai tolto subito dal corso "${course}" e non potrai cambiare idea da solo.`
            : "Sei sicuro? Per tornare nel corso dovrai contattare la direzione."}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="prereg-no-cancel">Annulla</AlertDialogCancel>
        {step === 1
          ? <Button data-testid="prereg-no-next" variant="destructive" onClick={() => setStep(2)}>Continua</Button>
          : <AlertDialogAction data-testid="prereg-no-confirm" className="bg-destructive text-white hover:bg-destructive/90" onClick={onConfirm}>Sì, non continuo</AlertDialogAction>}
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const PreregItem = ({ box, onSaved }) => {
  const [note, setNote] = useState(box.note || "");
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const id = slug(box.year);
  const send = async (answer) => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/prereg", { year: box.year, answer, note });
      toast.success(answer === "yes" ? "Preiscrizione registrata" : "Risposta registrata"); onSaved(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); setStep(0); }
  };
  return (
    <Card className="p-6 bg-card border-2 border-primary/40 space-y-4" data-testid={`prereg-box-${id}`}>
      <h2 className="text-2xl flex items-start gap-2"><GraduationCap size={22} className="text-primary shrink-0 mt-1" />
        {box.answer ? `Preiscrizione ${box.year}` : `Vuoi pre-iscriverti a ${box.course} per l'anno ${box.year}?`}</h2>
      {box.answer === "yes" && <p data-testid={`prereg-summary-yes-${id}`} className="flex items-start gap-2 text-green-700"><CheckCircle2 size={18} className="shrink-0 mt-0.5" /> Sei preiscritto a <strong>{box.course}</strong> per l'anno {box.year}. La vera iscrizione si completa poi con il modulo firmato.</p>}
      {box.answer === "no" && <p data-testid={`prereg-summary-no-${id}`} className="flex items-start gap-2 text-muted-foreground"><XCircle size={18} className="shrink-0 mt-0.5" /> Hai indicato che non continui il corso {box.course} nel {box.year}. Per ripensarci contatta la direzione.</p>}
      {box.info_text && <p className="text-sm text-muted-foreground whitespace-pre-line break-words" data-testid={`prereg-info-${id}`}>{box.info_text}</p>}
      {box.answer !== "no" && (
        <>
          <div><Label className="text-xs">Note per la direzione (facoltative, es. richiesta di cambio corso)</Label>
            <Textarea data-testid={`prereg-note-${id}`} rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 bg-white" /></div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button data-testid={`prereg-yes-${id}`} disabled={busy} onClick={() => send("yes")}>{box.answer === "yes" ? "Aggiorna la nota" : "Sì, mi preiscrivo"}</Button>
            <Button data-testid={`prereg-no-${id}`} variant="outline" disabled={busy} onClick={() => setStep(1)} className="text-destructive hover:text-destructive">No, non continuo</Button>
          </div>
          <p className="text-xs text-muted-foreground">È un'indicazione non vincolante: nessun pagamento ora.</p>
        </>
      )}
      {box.answer === "no" && box.note && <p className="text-xs text-muted-foreground whitespace-pre-line break-words">La tua nota: {box.note}</p>}
      <NoConfirm step={step} setStep={setStep} course={box.course} onConfirm={() => send("no")} />
    </Card>
  );
};

export const PreregBox = () => {
  const [boxes, setBoxes] = useState([]);
  useEffect(() => { api.get("/me/prereg").then((r) => setBoxes(r.data)).catch(() => {}); }, []);
  if (!boxes.length) return null;
  return (
    <div className="space-y-4" data-testid="prereg-section">
      {boxes.map((b) => <PreregItem key={`${b.year}|${b.answer}`} box={b} onSaved={(nb) => setBoxes((s) => s.map((x) => (x.year === nb.year ? nb : x)))} />)}
    </div>
  );
};
